import { useEffect, useState } from "react";
import { CalendarDays, Check, ChevronDown, Mail, Pencil, Phone, X } from "lucide-react";
import { formatMoney } from "@/lib/currency";
import { useTenantMe } from "@/lib/tenant-me";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  useCustomerBookings,
  useCustomerDetail,
  usePatchCustomer,
  type Customer,
  type CustomerCustomFieldValue,
} from "@/lib/customers";
import { useCustomerTags } from "@/lib/customer-tags";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter } from "./Drawer";
import { Tabs } from "./Tabs";
import { StatusBadge } from "./StatusBadge";
import { EmptyState } from "./EmptyState";
import { FieldPair } from "./DataTable";
import { useToast } from "./Toast";
import { BookingSidePanel } from "./BookingSidePanel";
import { CustomFieldInput } from "./CustomFieldInput";
import { formatPreferredDate } from "@/lib/booking-date";

/**
 * Right-rail Client Detail panel. Same chrome family as `BookingSidePanel`
 * + `InventoryItemPanel` — there's no Pencil frame for a client detail so
 * we model after the existing booking side panel.
 *
 * Tabs:
 *   * **Overview** — read-only fields with an "Edit" toggle that PATCHes via
 *                    `PATCH /api/v1/customers/{id}`.
 *   * **Bookings** — `useCustomerBookings(id)` (server scope via the new
 *                    `?customerId=` filter). Row click opens the existing
 *                    `<BookingSidePanel>` on top of this one.
 */
type TabKey = "overview" | "bookings";

export function ClientDetailPanel({
  customerId,
  onClose,
}: {
  customerId: string | null;
  onClose: () => void;
}) {
  const open = !!customerId;
  const [tab, setTab] = useState<TabKey>("overview");
  const [openBookingId, setOpenBookingId] = useState<string | null>(null);

  const detail = useCustomerDetail(customerId);
  const customer = detail.data ?? null;
  const tenant = useTenantMe();
  const tenantCurrency = tenant.data?.tenant.currency ?? "PHP";

  useEffect(() => {
    if (customerId) setTab("overview");
  }, [customerId]);

  const fullName =
    [customer?.firstName, customer?.lastName].filter(Boolean).join(" ") ||
    customer?.email ||
    "(unnamed)";

  const initials = (() => {
    const a = (customer?.firstName ?? "").charAt(0).toUpperCase();
    const b = (customer?.lastName ?? "").charAt(0).toUpperCase();
    if (a || b) return (a + b) || a || b;
    return (customer?.email ?? "?").charAt(0).toUpperCase();
  })();

  return (
    <>
      <Drawer open={open} onClose={onClose} width="lg">
        {detail.isLoading && !customer && (
          <DrawerBody className="px-5 py-6">
            <p className="text-sm text-muted-foreground">Loading…</p>
          </DrawerBody>
        )}

        {detail.isError && (
          <DrawerBody className="px-5 py-6">
            <p className="text-sm text-destructive">
              {(detail.error as Error).message}
            </p>
          </DrawerBody>
        )}

        {customer && (
          <>
            <DrawerHeader
              title={
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 rounded-full bg-primary-light text-primary flex items-center justify-center text-sm font-semibold shrink-0">
                    {initials}
                  </div>
                  <span className="truncate">{fullName}</span>
                </div>
              }
              subtitle={customer.email ?? customer.phone ?? "No contact details"}
              onClose={onClose}
            />

            <div className="px-5 py-4 grid grid-cols-3 gap-4 border-b border-border">
              <Stat
                label="Lifetime value"
                value={formatMoney(tenantCurrency, customer.lifetimeValue)}
              />
              <Stat
                label="First seen"
                value={
                  customer.firstSeenAt
                    ? new Date(customer.firstSeenAt).toLocaleDateString()
                    : "—"
                }
              />
              <Stat
                label="Status"
                value={<StatusBadge label="active" />}
              />
            </div>

            <Tabs<TabKey>
              value={tab}
              onChange={setTab}
              className="mt-3 px-5"
              items={[
                { key: "overview", label: "Overview" },
                { key: "bookings", label: "Bookings" },
              ]}
            />

            <DrawerBody className="px-5 py-5">
              {tab === "overview" && <OverviewTab customer={customer} />}
              {tab === "bookings" && (
                <BookingsTab
                  customerId={customer.id}
                  onOpenBooking={setOpenBookingId}
                />
              )}
            </DrawerBody>

            <DrawerFooter className="justify-between">
              {customer.email ? (
                <a
                  href={`mailto:${customer.email}`}
                  className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                >
                  <Mail className="h-3.5 w-3.5" /> Email client
                </a>
              ) : (
                <span />
              )}
              <Button variant="outline" size="sm" onClick={onClose}>
                Close
              </Button>
            </DrawerFooter>
          </>
        )}
      </Drawer>

      <BookingSidePanel
        bookingId={openBookingId}
        onClose={() => setOpenBookingId(null)}
      />
    </>
  );
}

function OverviewTab({ customer }: { customer: Customer }) {
  const [editing, setEditing] = useState(false);
  const patch = usePatchCustomer(customer.id);
  const toast = useToast();
  const tagsQ = useCustomerTags();
  const allTags = tagsQ.data?.tags ?? [];
  const customFields = customer.customFieldValues ?? [];

  const [firstName, setFirstName] = useState(customer.firstName ?? "");
  const [lastName, setLastName] = useState(customer.lastName ?? "");
  const [email, setEmail] = useState(customer.email ?? "");
  const [phone, setPhone] = useState(customer.phone ?? "");
  const [notes, setNotes] = useState(customer.notes ?? "");
  const [tagIds, setTagIds] = useState<string[]>(
    customer.tags?.map((t) => t.id) ?? [],
  );
  const [customDraft, setCustomDraft] = useState<Record<string, string>>(
    () => initial(customFields),
  );
  const [tagPickerOpen, setTagPickerOpen] = useState(false);

  // Re-seed when a different customer opens or scope changes via tag edits.
  useEffect(() => {
    setCustomDraft(initial(customFields));
  }, [customer.id, customFields]);

  const start = () => {
    setFirstName(customer.firstName ?? "");
    setLastName(customer.lastName ?? "");
    setEmail(customer.email ?? "");
    setPhone(customer.phone ?? "");
    setNotes(customer.notes ?? "");
    setTagIds(customer.tags?.map((t) => t.id) ?? []);
    setCustomDraft(initial(customFields));
    setEditing(true);
  };

  const save = () => {
    const customFieldValues = customFields.map((f) => ({
      fieldId: f.fieldId,
      value: customDraft[f.fieldId]?.trim() || null,
    }));
    patch.mutate(
      {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        notes,
        tagIds,
        customFieldValues: customFields.length ? customFieldValues : undefined,
      },
      {
        onSuccess: () => {
          setEditing(false);
          toast.success("Saved");
        },
        onError: (e) => toast.error("Couldn't save", (e as Error).message),
      },
    );
  };

  const toggleTag = (id: string) => {
    setTagIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  if (editing) {
    return (
      <section className="rounded-lg border bg-card">
        <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
            Edit client
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={save} disabled={patch.isPending}>
              {patch.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
        <div className="px-4 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name">
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Field>
            <Field label="Last name">
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Field>
          </div>
          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field label="Phone">
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </Field>
          <Field label="Notes">
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Allergies, preferences, anything operators should know."
            />
          </Field>
          <Field label="Tags">
            {tagIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-1.5">
                {tagIds.map((id) => {
                  const t = allTags.find((x) => x.id === id);
                  if (!t) return null;
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs"
                    >
                      {t.name}
                      <button
                        type="button"
                        onClick={() => toggleTag(id)}
                        aria-label={`Remove ${t.name}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <Popover open={tagPickerOpen} onOpenChange={setTagPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full justify-between"
                >
                  <span className="text-muted-foreground">
                    {tagIds.length === 0 ? "Add tags…" : `${tagIds.length} selected`}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="p-0 w-[var(--radix-popover-trigger-width)]"
                align="start"
              >
                <Command>
                  <CommandInput placeholder="Search tags…" />
                  <CommandList>
                    <CommandEmpty>
                      No tags. Define some in Settings → Custom fields → Customers.
                    </CommandEmpty>
                    <CommandGroup>
                      {allTags.map((t) => {
                        const isOn = tagIds.includes(t.id);
                        return (
                          <CommandItem
                            key={t.id}
                            value={t.name}
                            onSelect={() => toggleTag(t.id)}
                          >
                            <Check
                              className={`mr-2 h-3.5 w-3.5 ${
                                isOn ? "opacity-100" : "opacity-0"
                              }`}
                            />
                            <span>{t.name}</span>
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </Field>

          {customFields.length > 0 && (
            <div className="border-t border-border pt-3 space-y-3">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
                Custom fields
              </p>
              {customFields.map((f) => (
                <Field
                  key={f.fieldId}
                  label={`${f.label}${f.required ? " *" : ""}`}
                >
                  <CustomFieldInput
                    field={{ type: f.type, options: f.options, required: f.required }}
                    value={customDraft[f.fieldId] ?? ""}
                    onChange={(v) =>
                      setCustomDraft((d) => ({ ...d, [f.fieldId]: v }))
                    }
                  />
                </Field>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-lg border bg-card">
      <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">
          Client details
        </p>
        <button
          type="button"
          onClick={start}
          className="text-xs text-primary hover:underline inline-flex items-center gap-1"
        >
          <Pencil className="h-3 w-3" /> Edit
        </button>
      </div>
      <div className="px-4 py-3 space-y-1">
        <FieldPair label="First name" value={customer.firstName ?? "—"} />
        <FieldPair label="Last name" value={customer.lastName ?? "—"} />
        <FieldPair
          label="Email"
          value={
            customer.email ? (
              <a
                href={`mailto:${customer.email}`}
                className="text-primary hover:underline inline-flex items-center gap-1.5"
              >
                <Mail className="h-3 w-3" />
                {customer.email}
              </a>
            ) : (
              "—"
            )
          }
        />
        <FieldPair
          label="Phone"
          value={
            customer.phone ? (
              <span className="inline-flex items-center gap-1.5">
                <Phone className="h-3 w-3 text-muted-foreground" />
                {customer.phone}
              </span>
            ) : (
              "—"
            )
          }
        />
        <FieldPair
          label="Notes"
          value={
            customer.notes ? (
              <span className="whitespace-pre-wrap">{customer.notes}</span>
            ) : (
              <span className="text-muted-foreground italic">No notes yet.</span>
            )
          }
        />
        <FieldPair
          label="Tags"
          value={
            customer.tags && customer.tags.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {customer.tags.map((t) => (
                  <span
                    key={t.id}
                    className="inline-flex items-center rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs"
                  >
                    {t.name}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-muted-foreground italic">No tags.</span>
            )
          }
        />
        {customFields.map((f) => (
          <FieldPair
            key={f.fieldId}
            label={f.label}
            value={
              f.value ? (
                f.type === "checkbox" ? (
                  f.value === "true" ? "Yes" : "No"
                ) : (
                  f.value
                )
              ) : (
                <span className="text-muted-foreground italic">—</span>
              )
            }
          />
        ))}
      </div>
    </section>
  );
}

// Matches `BookingSummary` from the operator-side `GET /api/v1/bookings`
// list endpoint: flat `serviceName` + `price`, NOT the nested
// `BookingDetail` shape used by `/api/v1/bookings/{id}`.
type BookingRow = {
  id?: string;
  preferredDate?: string | null;
  serviceName?: string | null;
  status?: string;
  price?: number | string;
  currency?: string | null;
};

function BookingsTab({
  customerId,
  onOpenBooking,
}: {
  customerId: string;
  onOpenBooking: (id: string) => void;
}) {
  const list = useCustomerBookings(customerId);

  if (list.isLoading) {
    return <p className="text-sm text-muted-foreground">Loading bookings…</p>;
  }

  if (list.isError) {
    return (
      <p className="text-sm text-destructive">{(list.error as Error).message}</p>
    );
  }

  const rows = ((list.data as { content?: BookingRow[] } | undefined)?.content ??
    []) as BookingRow[];

  if (rows.length === 0) {
    return (
      <EmptyState
        bare
        icon={CalendarDays}
        title="No bookings yet"
        description="When this client books a service, it'll show up here."
      />
    );
  }

  return (
    <ol className="rounded-lg border bg-card divide-y">
      {rows.map((b) => (
        <li
          key={b.id}
          className="px-4 py-3 flex items-center gap-3 text-sm hover:bg-muted/30 cursor-pointer"
          onClick={() => b.id && onOpenBooking(b.id)}
        >
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-primary-light text-primary">
            <CalendarDays className="h-3.5 w-3.5" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate">
              {b.serviceName ?? "(service removed)"}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {b.preferredDate ? formatPreferredDate(b.preferredDate) : "—"}
              {b.price != null && (
                <>
                  {" · "}
                  {formatMoney(b.currency, b.price)}
                </>
              )}
            </p>
          </div>
          {b.status && <StatusBadge label={b.status} />}
        </li>
      ))}
    </ol>
  );
}

function Stat({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </p>
      <div className="mt-1 text-sm font-semibold">{value}</div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

/** Seed the edit-mode draft from the embedded value snapshots. */
function initial(fields: CustomerCustomFieldValue[]): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of fields) out[f.fieldId] = f.value ?? "";
  return out;
}

